import { Input } from 'sr/helpers';
import { useIntl } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import { ReactDatePicker } from 'sr/helpers/ui-components/ReactDatePicker';
import { ReactSelect } from 'sr/helpers/ui-components/ReactSelect';
import { RadioInput } from 'sr/helpers/ui-components/RadioInput';
import { useFormik } from 'formik';
import React, { useState, forwardRef, ForwardRefRenderFunction, useEffect } from 'react';
import { createTraning } from 'sr/utils/api/cleaner/createTraning';
import { getUserDetails } from 'sr/helpers/functions/getUserDetails';
import { CleanerComponentProps } from './CleanerComponentProps';
import { getTraining } from 'sr/utils/api/cleaner/getTraining';
import { DateFormat } from 'sr/helpers/functions/DateFormat';
import { updateTraining } from 'sr/utils/api/cleaner/updateTraining';
import ContentWithSpinner from 'sr/layout/common/ContentWithSpiner';
import { ButtonAuth } from 'sr/helpers/ui-components/ButtonAuth';
import { LoaderFullScreen } from 'sr/layout/common/LoaderFullScreen';
import { renderRequiredLabel } from 'sr/utils/helper';
import { getGeneralType } from 'sr/utils/api/getGeneralType';
import { buildTrainingPayload } from 'sr/utils/api/cleaner/trainingPayload';
import { deleteTraining } from 'sr/utils/api/cleaner/deleteTraining';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';

const OTHER_TRAINING_CERTIFICATION_OPTION = 'Other'

const TRAINING_TYPE_OPTIONS = [
  {
    value: 'Training',
    labelId: 'CLEANER.REGISTER.TRAINING_CERTIFICATIONS.TYPE.OPTION.TRAINING',
  },
  {
    value: 'Certification',
    labelId: 'CLEANER.REGISTER.TRAINING_CERTIFICATIONS.TYPE.OPTION.CERTIFICATION',
  },
]

const normalizeTrainingValue = (value:string = '') => value.trim().toLowerCase()

const validateTrainingData = (values:any, existingData:any[] = [], currentId:any = '') => {
  const errors:any = {}

  if (!values?.training_type) {
    errors.training_type = 'INPUT.VALIDATION.TRAINING.TYPE'
  }

  if (values?.training_type && !values?.training_selection) {
    errors.training_selection = 'INPUT.VALIDATION.TRAINING.SELECTION'
  }

  if (
    values?.training_selection === OTHER_TRAINING_CERTIFICATION_OPTION &&
    !values?.other_training_name?.trim()
  ) {
    errors.other_training_name = 'INPUT.VALIDATION.TRAINING.OTHER.NAME'
  }

  const selectedTrainingValue = values?.training_selection === OTHER_TRAINING_CERTIFICATION_OPTION
    ? values?.other_training_name
    : values?.training_selection

  if (values?.training_type && selectedTrainingValue?.trim()) {
    const normalizedType = values?.training_type?.toLowerCase()
    const normalizedValue = normalizeTrainingValue(selectedTrainingValue)
    const isDuplicate = existingData?.some((item) => {
      const itemType = item?.documentType
      const itemValue = normalizeTrainingValue(item?.documentName)
      const itemId = item?.id

      return itemId !== currentId && itemType === normalizedType && itemValue === normalizedValue
    })

    if (isDuplicate) {
      if (values?.training_selection === OTHER_TRAINING_CERTIFICATION_OPTION) {
        errors.other_training_name = 'INPUT.VALIDATION.TRAINING.DUPLICATE'
      }
      else {
        errors.training_selection = 'INPUT.VALIDATION.TRAINING.DUPLICATE'
      }
    }
  }

  return errors
}

const mapGeneralTypeOptions = (items:any[] = []) => {
  return items
    .map((item) => ({
      value: item.title,
      label: item.title,
    }))
    .sort((a:any, b:any) => {
      if (a?.value === OTHER_TRAINING_CERTIFICATION_OPTION) {
        return 1
      }

      if (b?.value === OTHER_TRAINING_CERTIFICATION_OPTION) {
        return -1
      }

      return a.label.localeCompare(b.label)
    })
}

const CleanerTraining: ForwardRefRenderFunction<any, CleanerComponentProps> = (
  { HandleStepper = null, page = '', type = '', dataAddedHandle=null, id='' },
  ref
) => {
  const [AddMoreClicked,setAddMoreClicked] = useState(false)
  const [dateAttended, setDateAttended] = useState(new Date())
  const [description, setDescription] = useState("")
  const [trainingType, setTrainingType] = useState("")
  const [trainingSelection, setTrainingSelection] = useState("")
  const [otherTrainingName, setOtherTrainingName] = useState("")
  const [loader, setLoader] = useState(false)
  const [data, setData] = useState(null)
  const [buttonLoader, setButtonLoader] = useState(false)
  const [userData, setUserData] = useState(null)
  const [trainingOptions, setTrainingOptions] = useState<any[]>([])
  const [certificationOptions, setCertificationOptions] = useState<any[]>([])
  const [editingTrainingData, setEditingTrainingData] = useState<any>(null)
  const [editingTrainingId, setEditingTrainingId] = useState("")
  const intl = useIntl()
  const isPopup = page === 'popup'

  useEffect(()=> {
    const getdetails = getUserDetails()
    setUserData(getdetails)
  },[])

  useEffect(() => {
    const fetchGeneralTypeOptions = async () => {
      const [trainingResponse, certificationResponse] = await Promise.all([
        getGeneralType('cleanerTrainings'),
        getGeneralType('cleanerCertifications'),
      ])

      const trainingData = mapGeneralTypeOptions(trainingResponse?.data || [])
      const certificationData = mapGeneralTypeOptions(certificationResponse?.data || [])

      setTrainingOptions(trainingData)
      setCertificationOptions(certificationData)
    }

    fetchGeneralTypeOptions()
  }, [intl])

  const initialValues = {
    cleaner_id:userData?.user_id,
    date_attended:dateAttended,
    description:description,
    training_type:trainingType,
    training_selection:trainingSelection,
    other_training_name:otherTrainingName
  }

  const initialValuesPut = {
    cleaner_id:userData?.user_id,
    date_attended:dateAttended,
    description:description,
    training_type:trainingType,
    training_selection:trainingSelection,
    other_training_name:otherTrainingName,
    id:id
  }

  useEffect(()=> {
    const fetchData = async () => {
      setLoader(true)
      try {
        const trainingData = await getTraining(undefined,id)
        if(trainingData?.success===true){
          setDateAttended(trainingData?.data?.date_attended)
          setDescription(trainingData?.data?.description)
          setEditingTrainingData(trainingData?.data)
          setLoader(false)
        }
      }
      catch(e:any){
        setLoader(false)
      }
    }
    if(page === 'popup' && type==='put'){
      fetchData()
    }
  },[id])

  const {handleSubmit, errors, submitForm, setFieldValue, submitCount} = useFormik({
    initialValues:page === 'popup' && type==='put' ? initialValuesPut:initialValues,
    enableReinitialize:true,
    validate:(values) => validateTrainingData(values, data || [], editingTrainingId || id),
    onSubmit: async (values, helpers)=> {
      setButtonLoader(true)
      let result
      const payload = buildTrainingPayload(values, userData?.user_id)

      if(page === 'popup' && type==='put'){
        setFieldValue('id', id)
        result = await updateTraining({
          ...payload,
          id:id
        })
      }
      else if(editingTrainingId){
        result = await updateTraining({
          ...payload,
          id:editingTrainingId
        })
      }
      else {
        result = await createTraning(payload)
      }

      if(result) {
        setButtonLoader(false)
        if(page === 'popup' ){
          dataAddedHandle(true)
        }
        if(AddMoreClicked) {
          helpers.resetForm()
          setDateAttended(null)
          setDescription("")
          setTrainingType("")
          setTrainingSelection("")
          setOtherTrainingName("")
          fetchTrainingData(userData?.user_id)
          setAddMoreClicked(false)
        }
        else if(editingTrainingId) {
          resetTrainingForm()
          fetchTrainingData(userData?.user_id)
        }
        else {
          if (HandleStepper !== null) {
            HandleStepper(4);
          }
        }
      }
      else {
        setButtonLoader(false)
      }
    },
  })

  React.useImperativeHandle(ref, () => ({
    submitForm,
  }))

  const resetTrainingForm = () => {
    setEditingTrainingId("")
    setEditingTrainingData(null)
    setDateAttended(new Date())
    setDescription("")
    setTrainingType("")
    setTrainingSelection("")
    setOtherTrainingName("")
    setFieldValue('training_type', '')
    setFieldValue('training_selection', '')
    setFieldValue('other_training_name', '')
    setFieldValue('date_attended', new Date())
    setFieldValue('description', '')
  }

  const populateTrainingForm = (item:any) => {
    const selectedType = item?.documentType === 'certification' ? 'Certification' : 'Training'
    const selectedName = item?.documentName || ''
    const selectedOptions = selectedType === 'Certification' ? certificationOptions : trainingOptions
    const hasSelectedOption = selectedOptions?.some(
      (option) => normalizeTrainingValue(option?.value) === normalizeTrainingValue(selectedName)
    )

    setEditingTrainingId(item?.id || "")
    setEditingTrainingData(item)
    setTrainingType(selectedType)
    setDateAttended(item?.date_attended || new Date())
    setDescription(item?.description || "")
    setFieldValue('training_type', selectedType)
    setFieldValue('date_attended', item?.date_attended || new Date())
    setFieldValue('description', item?.description || "")
    setAddMoreClicked(false)

    if (!selectedName) {
      setTrainingSelection('')
      setOtherTrainingName('')
      setFieldValue('training_selection', '')
      setFieldValue('other_training_name', '')
      return
    }

    if (hasSelectedOption) {
      setTrainingSelection(selectedName)
      setOtherTrainingName('')
      setFieldValue('training_selection', selectedName)
      setFieldValue('other_training_name', '')
    }
    else {
      setTrainingSelection(OTHER_TRAINING_CERTIFICATION_OPTION)
      setOtherTrainingName(selectedName)
      setFieldValue('training_selection', OTHER_TRAINING_CERTIFICATION_OPTION)
      setFieldValue('other_training_name', selectedName)
    }
  }

  const handleDeleteTraining = async (item:any) => {
    if (!item?.id) return

    setButtonLoader(true)
    const result = await deleteTraining({ id:item.id })
    setButtonLoader(false)

    if (result?.success === true) {
      if (editingTrainingId === item.id) {
        resetTrainingForm()
      }
      fetchTrainingData(userData?.user_id)
    }
  }

  useEffect(() => {
    if (!editingTrainingData || (!isPopup && !editingTrainingId) || (isPopup && type !== 'put')) {
      return
    }

    const selectedType = editingTrainingData?.documentType === 'certification' ? 'Certification' : 'Training'
    const selectedName = editingTrainingData?.documentName || ''
    const selectedOptions = selectedType === 'Certification' ? certificationOptions : trainingOptions
    const hasSelectedOption = selectedOptions?.some(
      (item) => normalizeTrainingValue(item?.value) === normalizeTrainingValue(selectedName)
    )

    setTrainingType(selectedType)

    if (!selectedName) {
      setTrainingSelection('')
      setOtherTrainingName('')
      return
    }

    if (hasSelectedOption) {
      setTrainingSelection(selectedName)
      setOtherTrainingName('')
    }
    else {
      setTrainingSelection(OTHER_TRAINING_CERTIFICATION_OPTION)
      setOtherTrainingName(selectedName)
    }
  }, [editingTrainingData, certificationOptions, editingTrainingId, isPopup, trainingOptions, type])

  const fetchTrainingData = async (cleanerId, showLoader = true) => {
    if (showLoader) {
      setButtonLoader(true)
    }
    try {
      const trainingData = await getTraining(cleanerId, undefined);
      if (trainingData?.success) {
        setData(trainingData.data);
      }
    }
    catch(e:any){
    }
    finally {
      if (showLoader) {
        setButtonLoader(false)
      }
    }
  };

  useEffect(()=>{
    if(userData?.user_id){
      fetchTrainingData(userData?.user_id, !isPopup)
    }
  },[isPopup, userData?.user_id])

  const sortTrainingItemsByDate = (items:any[] = []) => {
    return [...items].sort((a:any, b:any) => {
      const aDate = a?.date_attended ? new Date(a.date_attended).getTime() : 0
      const bDate = b?.date_attended ? new Date(b.date_attended).getTime() : 0

      return bDate - aDate
    })
  }
  const trainingListData = sortTrainingItemsByDate(
    data?.filter((item) => item?.documentType !== 'certification') || []
  )
  const certificationListData = sortTrainingItemsByDate(
    data?.filter((item) => item?.documentType === 'certification') || []
  )
  const usedTrainingValues = new Set(
    (trainingListData || []).map((item) => normalizeTrainingValue(item?.documentName))
  )
  const usedCertificationValues = new Set(
    (certificationListData || []).map((item) => normalizeTrainingValue(item?.documentName))
  )
  const formattedTrainingOptions = (
    trainingType === 'Certification'
      ? certificationOptions
      : trainingOptions
  )?.filter((item) => {
    if (item?.value === OTHER_TRAINING_CERTIFICATION_OPTION || item?.value === trainingSelection) {
      return true
    }

    const usedValues = trainingType === 'Certification' ? usedCertificationValues : usedTrainingValues
    return !usedValues.has(normalizeTrainingValue(item?.value))
  })
  const renderSavedList = (items:any[] = [], sectionTitle:any, dateTitle:any) => {
    if (!items?.length) {
      return null
    }

    return (
      <div className='mt-4'>
        <div className='flex items-center justify-between'>
          <div className='text-[15px] font-bold text-gray-900'>{sectionTitle}</div>
          <div className='rounded-full bg-gray-100 px-2.5 py-0.5 text-[12px] font-medium text-gray-600'>
            {items?.length}
          </div>
        </div>
        <div className='mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white'>
        {items?.map((item, index)=> (
          <div
            key={item?.id}
            className={`bg-white px-3 py-3 ${
              index !== items.length - 1 ? 'border-b border-gray-200' : ''
            }`}
          >
            <div className='flex items-start justify-between gap-3'>
              <div className='text-[16px] font-[700] break-words'>
                {item?.documentName}
              </div>
              <div className='flex flex-shrink-0 gap-2'>
                <button
                  type='button'
                  className='flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-[#0078ae] hover:bg-gray-50'
                  onClick={()=> populateTrainingForm(item)}
                  title='Edit item'
                >
                  <FontAwesomeIcon icon={faPencil} />
                </button>
                <button
                  type='button'
                  className='flex h-8 w-8 items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50'
                  onClick={()=> handleDeleteTraining(item)}
                  title='Delete item'
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
            <div className='mt-2 border-t border-gray-100 pt-2'>
              <div className='text-[14px] font-[500] text-gray-800 mt-1'>
                {dateTitle} - {DateFormat(item?.date_attended)}
              </div>
              <div className='text-[14px] font-[500] text-gray-800 mt-1'>
                {intl.formatMessage({
                  id:'CLEANER.PROFILE.TRAINING.INPUT.DESCRIPTION.TITLE',
                  defaultMessage:'Description',
                })}
                {' '} - {item?.description || '-'}
              </div>
            </div>
          </div>
        ))}
        </div>
      </div>
    )
  }
  const trainingSelectionLabel = trainingType === 'Certification'
    ? renderRequiredLabel(
      intl.formatMessage({
        id:'CLEANER.REGISTER.TRAINING_CERTIFICATIONS.INPUT.CERTIFICATION.TITLE',
        defaultMessage:'Certification',
      })
    )
    : renderRequiredLabel(
      intl.formatMessage({
        id:'CLEANER.REGISTER.TRAINING_CERTIFICATIONS.INPUT.TRAINING.TITLE',
        defaultMessage:'Training',
      })
    )
  const trainingSelectionPlaceholder = intl.formatMessage({
    id:trainingType === 'Certification'
      ? 'CLEANER.REGISTER.TRAINING_CERTIFICATIONS.INPUT.CERTIFICATION.PLACEHOLDER'
      : 'CLEANER.REGISTER.TRAINING_CERTIFICATIONS.INPUT.TRAINING.PLACEHOLDER',
    defaultMessage:trainingType === 'Certification' ? 'Select certification' : 'Select training',
  })
  const dateLabel = trainingType === 'Certification'
    ? intl.formatMessage({
      id:'CLEANER.REGISTER.TRAINING_CERTIFICATIONS.DATE.RECEIVED',
      defaultMessage:'Date Received',
    })
    : intl.formatMessage({
      id:'CLEANER.PROFILE.TRAINING.DATE.ATTENDED',
      defaultMessage:'Date Attended',
    })
  const showValidationErrors = submitCount > 0

  return (
    <ContentWithSpinner loader={loader}>
      {buttonLoader && <LoaderFullScreen />}
      <form className='mt-5' onSubmit={handleSubmit}>
        <div className='mt-10'>
          <label className='mt-2 text-gray-700 text-sm'>
            {renderRequiredLabel(
              intl.formatMessage({
                id:'CLEANER.REGISTER.TRAINING_CERTIFICATIONS.TYPE.TITLE',
                defaultMessage:'Type',
              })
            )}
          </label>
          <div className='mt-2 flex flex-col'>
            {TRAINING_TYPE_OPTIONS.map((item)=> (
              <RadioInput
                key={item.value}
                name='training_type'
                value={item.value}
                checked={trainingType === item.value}
                onChange={(e)=> {
                  setTrainingType(e.target.value)
                  setTrainingSelection("")
                  setOtherTrainingName("")
                  setFieldValue('training_type', e.target.value)
                  setFieldValue('training_selection', '')
                  setFieldValue('other_training_name', '')
                }}
                label={intl.formatMessage({
                  id:item.labelId,
                  defaultMessage:item.value,
                })}
              />
            ))}
          </div>
          {showValidationErrors && typeof errors?.training_type === 'string' && <p className='mt-1 text-red-400 md:text-sm sm:text-[.65rem]'>
            {intl.formatMessage({
              id:errors?.training_type,
              defaultMessage:'Please select a type',
            })}
          </p>}
        </div>

        {trainingType && (
          <div>
            <ReactSelect
              key={`${trainingType}-${trainingSelection || 'empty'}`}
              label={trainingSelectionLabel}
              type="text"
              onChange={(selectedOptions)=> {
                setTrainingSelection(selectedOptions?.value || '')
                setFieldValue('training_selection', selectedOptions?.value || '')
                if(selectedOptions?.value !== OTHER_TRAINING_CERTIFICATION_OPTION){
                  setOtherTrainingName("")
                  setFieldValue('other_training_name', '')
                }
              }}
              placeholder={trainingSelectionPlaceholder}
              options={formattedTrainingOptions}
              defaultValue={formattedTrainingOptions?.find((item) => item?.value === trainingSelection)}
            />
            {showValidationErrors && typeof errors?.training_selection === 'string' && <p className='mt-1 text-red-400 md:text-sm sm:text-[.65rem]'>
              {intl.formatMessage({
                id:errors?.training_selection,
                defaultMessage:'Please select a training or certification',
              })}
            </p>}
          </div>
        )}

        {trainingSelection === OTHER_TRAINING_CERTIFICATION_OPTION && (
          <>
            <Input
              value={otherTrainingName}
              label={renderRequiredLabel(
                intl.formatMessage({
                  id:trainingType === 'Certification'
                    ? 'CLEANER.REGISTER.TRAINING_CERTIFICATIONS.INPUT.OTHER.CERTIFICATION.TITLE'
                    : 'CLEANER.REGISTER.TRAINING_CERTIFICATIONS.INPUT.OTHER.TRAINING.TITLE',
                  defaultMessage:trainingType === 'Certification' ? 'Other Certification' : 'Other Training',
                })
              )}
              type="text"
              onChange={(e)=> {
                setOtherTrainingName(e.target.value)
                setFieldValue('other_training_name', e.target.value)
              }}
              name="other_training_name"
              placeholder={intl.formatMessage({
                id:trainingType === 'Certification'
                  ? 'CLEANER.REGISTER.TRAINING_CERTIFICATIONS.INPUT.OTHER.CERTIFICATION.PLACEHOLDER'
                  : 'CLEANER.REGISTER.TRAINING_CERTIFICATIONS.INPUT.OTHER.TRAINING.PLACEHOLDER',
                defaultMessage:trainingType === 'Certification' ? 'Enter certification name' : 'Enter training name',
              })}
            />
            {showValidationErrors && typeof errors?.other_training_name === 'string' && <p className='mt-1 text-red-400 md:text-sm sm:text-[.65rem]'>
              {intl.formatMessage({
                id:errors?.other_training_name,
                defaultMessage:'Please enter the training or certification name',
              })}
            </p>}
          </>
        )}

        <ReactDatePicker
          onChange={(date)=> {
            setDateAttended(date)
            setFieldValue('date_attended', date)
          }}
          selected={dateAttended}
          error={errors.date_attended}
          maxDate={new Date()}
          isDefaultSelected={dateAttended ? true : false}
          label={dateLabel}
        ></ReactDatePicker>

        <Input
          value={description}
          onChange = {(e)=> {
            setDescription(e.target.value)
            setFieldValue('description', e.target.value)
          }}
          error={errors.description}
          label={intl.formatMessage({
            id: 'CLEANER.PROFILE.TRAINING.INPUT.DESCRIPTION.TITLE',
            defaultMessage: 'Description',
          })}
          type="text"
          placeholder={intl.formatMessage({
            id: 'CLEANER.PROFILE.TRAINING.INPUT.DECRIPTION.PLACEHOLDER',
            defaultMessage: 'Enter Description',
          })}
        />

        {isPopup ? '' : (
          <>
            <div className='mt-5 flex space-x-5'>
              <ButtonAuth loader={buttonLoader} onClick={()=> setAddMoreClicked(false)}>
                {editingTrainingId ? 'Update Item' : <FormattedMessage id="REGISTER.BUTTON.SAVEANDCONTINUE" defaultMessage="Save and Continue" />}
              </ButtonAuth>

              {!editingTrainingId && (
                <ButtonAuth loader={buttonLoader} onClick={()=> setAddMoreClicked(true)}>
                  Add More
                </ButtonAuth>
              )}

              {editingTrainingId && (
                <button
                  type='button'
                  className='mt-5 mb-5 flex h-10 w-32 items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-4 text-sm font-bold text-gray-700 hover:bg-gray-100'
                  onClick={resetTrainingForm}
                >
                  <FontAwesomeIcon icon={faXmark} />
                  Cancel
                </button>
              )}

              {!editingTrainingId && <div className='rounded-full text-center
                bg-[#ff39b3] font-bold
                p-2
                text-white
                w-44
                cursor-pointer mb-5 mt-5
                hover:bg-navy'
                onClick={()=> HandleStepper(4)}>Skip</div>}
            </div>
            {data?.length>0 &&<>
              {renderSavedList(
                trainingListData,
                intl.formatMessage({
                  id:'CLEANER.REGISTER.TRAINING_CERTIFICATIONS.LIST.TRAININGS',
                  defaultMessage:'Trainings',
                }),
                intl.formatMessage({
                  id:'CLEANER.PROFILE.TRAINING.DATE.ATTENDED',
                  defaultMessage:'Date Attended',
                })
              )}
              {renderSavedList(
                certificationListData,
                intl.formatMessage({
                  id:'CLEANER.REGISTER.TRAINING_CERTIFICATIONS.LIST.CERTIFICATIONS',
                  defaultMessage:'Certifications',
                }),
                intl.formatMessage({
                  id:'CLEANER.REGISTER.TRAINING_CERTIFICATIONS.DATE.RECEIVED',
                  defaultMessage:'Date Received',
                })
              )}
            </>}
          </>
        )}
      </form>
    </ContentWithSpinner>
  )
}

export default forwardRef(CleanerTraining);
